import type { Metadata } from "next";
import type { ComparisonCard } from "@/components/cheap-home-insurance/CarrierComparisonCards";
import type { PartnerLogo } from "@/components/cheap-home-insurance/PartnersSection";

export type CarrierHomeLandingContent = {
  slug: string;
  path: string;
  pageTitle: string;
  metaDescription: string;
  carrierName: string;
  compareQuotesDescription: string;
  discountsDescription: string;
  partnersHeading: string;
  whyCompareIntro: string;
  noHiddenFeesDescription: string;
  comparisonImage?: PartnerLogo["path"];
  tabletComparisonImage?: PartnerLogo["path"];
  comparisonCards: ComparisonCard[];
  partnerLogos: PartnerLogo[];
};

const logo = (
  path: PartnerLogo["path"],
  alt: string,
  mobile: string,
  tablet: string,
  desktop: string,
): PartnerLogo => ({
  path,
  alt,
  mobileClassName: mobile,
  tabletClassName: tablet,
  desktopClassName: desktop,
});

const card = (
  path: ComparisonCard["path"],
  alt: string,
  heightClass: string,
): ComparisonCard => ({ path, alt, heightClass });

function landing(
  carrierName: string,
  slug: string,
  partnerLogos: PartnerLogo[],
  comparisonCards: ComparisonCard[],
): CarrierHomeLandingContent {
  return {
    slug,
    path: `/${slug}`,
    pageTitle: `${carrierName} Home Insurance`,
    metaDescription: `Compare ${carrierName} home insurance quotes with 100+ other top carriers through Guardian Service.`,
    carrierName,
    compareQuotesDescription: `Answer a couple of questions, we'll compare ${carrierName} homeowners insurance quotes with 100+ other top carriers.`,
    discountsDescription: `Compare ${carrierName} home and auto insurance bundles with other insurers to find the most discounts.`,
    partnersHeading: `${carrierName.toUpperCase()} AND 100+ TOP HOME INSURANCE CARRIERS, ALL IN ONE PLACE`,
    whyCompareIntro: `Guardian Service delivers serious savings with no hidden fees and no catch. Compare ${carrierName} homeowners insurance and other carrier options to find rates and discounts.`,
    noHiddenFeesDescription: `What you see is what you get. Compare ${carrierName} home insurance quotes alongside other carriers in one place.`,
    comparisonCards,
    partnerLogos,
  };
}

export const CARRIER_HOME_LANDINGS: Record<string, CarrierHomeLandingContent> =
  {
    "progressive-home-insurance": landing(
      "Progressive",
      "progressive-home-insurance",
      [
        logo(
          "images/Progressive_Logo.webp",
          "Progressive",
          "h-[28px] object-contain",
          "h-[28px] object-contain",
          "h-[25px] object-contain",
        ),
        logo(
          "images/Allstate_Logo.webp",
          "Allstate",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Liberty_Mutual_Logo.webp",
          "Liberty Mutual",
          "h-[36px] object-contain",
          "h-[36px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Farmers_Insurance_Logo.webp",
          "Farmers Insurance",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/The_Hartford_Logo-2.webp",
          "The Hartford",
          "h-[36px] object-contain col-span-2",
          "h-[32px] object-contain",
          "h-[29px] object-contain",
        ),
      ],
      [
        card("images/Progressive_Logo.webp", "Progressive", "h-5"),
        card("images/Farmers_Insurance_Logo.webp", "Farmers Insurance", "h-7"),
        card("images/Travelers_Logo.webp", "Travelers", "h-7"),
        card("images/Nationwide_Logo.webp", "Nationwide", "h-10"),
      ],
    ),
    "allstate-home-insurance": landing(
      "Allstate",
      "allstate-home-insurance",
      [
        logo(
          "images/Allstate_Logo.webp",
          "Allstate",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Progressive_Logo.webp",
          "Progressive",
          "h-[22px] object-contain",
          "h-[18px] object-contain",
          "h-[18px] object-contain",
        ),
        logo(
          "images/Liberty_Mutual_Logo.webp",
          "Liberty Mutual",
          "h-[36px] object-contain",
          "h-[36px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Farmers_Insurance_Logo.webp",
          "Farmers Insurance",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/The_Hartford_Logo-2.webp",
          "The Hartford",
          "h-[30px] object-contain",
          "h-[26px] object-contain",
          "h-[25px] object-contain",
        ),
      ],
      [
        card("images/Allstate_Logo.webp", "Allstate", "h-8"),
        card("images/Progressive_Logo.webp", "Progressive", "h-5"),
        card("images/Travelers_Logo.webp", "Travelers", "h-7"),
        card("images/Nationwide_Logo.webp", "Nationwide", "h-10"),
      ],
    ),
    "geico-home-insurance": landing(
      "Geico",
      "geico-home-insurance",
      [
        logo(
          "images/Geico_Logo.webp",
          "Geico",
          "h-[22px] object-contain",
          "h-[22px] object-contain",
          "h-[21px] object-contain",
        ),
        logo(
          "images/Progressive_Logo.webp",
          "Progressive",
          "h-[22px] object-contain",
          "h-[18px] object-contain",
          "h-[18px] object-contain",
        ),
        logo(
          "images/Liberty_Mutual_Logo.webp",
          "Liberty Mutual",
          "h-[36px] object-contain",
          "h-[36px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Farmers_Insurance_Logo.webp",
          "Farmers Insurance",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/The_Hartford_Logo-2.webp",
          "The Hartford",
          "h-[30px] object-contain",
          "h-[26px] object-contain",
          "h-[25px] object-contain",
        ),
      ],
      [
        card("images/Geico_Logo.webp", "Geico", "h-6"),
        card("images/Progressive_Logo.webp", "Progressive", "h-5"),
        card("images/Travelers_Logo.webp", "Travelers", "h-7"),
        card("images/Nationwide_Logo.webp", "Nationwide", "h-10"),
      ],
    ),
    "aaa-home-insurance": landing(
      "AAA",
      "aaa-home-insurance",
      [
        logo(
          "images/AAA_Insurance_Logo.webp",
          "AAA Insurance",
          "h-[46px] object-contain",
          "h-[42px] object-contain",
          "h-[40px] object-contain",
        ),
        logo(
          "images/Progressive_Logo.webp",
          "Progressive",
          "h-[22px] object-contain",
          "h-[18px] object-contain",
          "h-[18px] object-contain",
        ),
        logo(
          "images/Liberty_Mutual_Logo.webp",
          "Liberty Mutual",
          "h-[36px] object-contain",
          "h-[36px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Farmers_Insurance_Logo.webp",
          "Farmers Insurance",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/The_Hartford_Logo-2.webp",
          "The Hartford",
          "h-[30px] object-contain",
          "h-[26px] object-contain",
          "h-[25px] object-contain",
        ),
      ],
      [
        card("images/AAA_Insurance_Logo.webp", "AAA Insurance", "h-12"),
        card("images/Progressive_Logo.webp", "Progressive", "h-5"),
        card("images/Travelers_Logo.webp", "Travelers", "h-7"),
        card("images/Nationwide_Logo.webp", "Nationwide", "h-10"),
      ],
    ),
    "travelers-home-insurance": landing(
      "Travelers",
      "travelers-home-insurance",
      [
        logo(
          "images/Travelers_Logo.webp",
          "Travelers",
          "h-[34px] object-contain",
          "h-[34px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Allstate_Logo.webp",
          "Allstate",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Liberty_Mutual_Logo.webp",
          "Liberty Mutual",
          "h-[36px] object-contain",
          "h-[36px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/Farmers_Insurance_Logo.webp",
          "Farmers Insurance",
          "h-[32px] object-contain",
          "h-[32px] object-contain",
          "h-[31px] object-contain",
        ),
        logo(
          "images/The_Hartford_Logo-2.webp",
          "The Hartford",
          "h-[36px] object-contain col-span-2",
          "h-[32px] object-contain",
          "h-[29px] object-contain",
        ),
      ],
      [
        card("images/Travelers_Logo.webp", "Travelers", "h-7"),
        card("images/Farmers_Insurance_Logo.webp", "Farmers Insurance", "h-7"),
        card("images/Progressive_Logo.webp", "Progressive", "h-5"),
        card("images/Nationwide_Logo.webp", "Nationwide", "h-10"),
      ],
    ),
  };

export const CARRIER_HOME_LANDING_PATHS = Object.freeze(
  Object.values(CARRIER_HOME_LANDINGS).map((landing) => landing.path),
);

export function getCarrierHomeLanding(
  slug: string,
): CarrierHomeLandingContent | undefined {
  return CARRIER_HOME_LANDINGS[slug];
}

export function isCarrierHomeLandingPath(pathname: string): boolean {
  const normalized = pathname.split("?")[0].replace(/\/$/, "") || "/";
  return CARRIER_HOME_LANDING_PATHS.includes(normalized);
}

export function getCarrierHomeMetadata(slug: string): Metadata {
  const content = getCarrierHomeLanding(slug);
  if (!content) {
    throw new Error(`Unknown carrier home landing: ${slug}`);
  }

  return {
    title: `${content.pageTitle} | Guardian Service`,
    description: content.metaDescription,
    robots: {
      index: false,
      follow: false,
      nocache: true,
      googleBot: {
        index: false,
        follow: false,
        noimageindex: true,
        nosnippet: true,
        noarchive: true,
      },
    },
  };
}
